import { useState, useEffect,useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Radio,
  Space,
  Popconfirm,
  MessagePlugin,
  type PrimaryTableCol
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, LockOnIcon } from 'tdesign-icons-react';
import { userApi } from '../../api';
import type { AdminUser, Role } from '../../types';

const { FormItem } = Form;
const { Option } = Select;

export default function UserManage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [resetVisible, setResetVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const formRef = useRef<any>(null);
  const [resetForm] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  interface FetchParams {
    pageNum?: number;
    pageSize?: number;
  }
  const fetchUsers = async (params : FetchParams = {}) => {
    setLoading(true);
    try {
      const rawSearchParams  = searchForm.getFieldsValue?.(true) || {};
      // 清洗参数：移除 undefined, null 和空字符串
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      const res = await userApi.getList({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        setUsers(res.data.list);
        setPagination({
          ...pagination,
          total: res.data.total,
          ...(params.pageNum && { current: params.pageNum }),
          ...(params.pageSize && { pageSize: params.pageSize }),
        });
      }
    } catch (error) {
      MessagePlugin.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await userApi.getRoles();
      if (res.code === 200) setRoles(res.data);
    } catch (error) {
      MessagePlugin.error('获取角色列表失败');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const resetFormState = () => {
    setCurrentUser(null);
    // 如果 formRef 有 reset 方法，也可以调用
    if (formRef.current) {
      formRef.current.reset();
    }
  };
  const handleAdd = () => {
    resetFormState();
    setVisible(true);
  };

  const handleEdit = (record: AdminUser) => {
    setCurrentUser(record);
    setVisible(true);
  };

  const handleResetPassword = (record: AdminUser) => {
    setCurrentUser(record);
    resetForm.reset();
    setResetVisible(true);
  };

  // ===================== 核心：完全手动控制校验和提交 =====================
  const handleFormSubmit = async () => {
    // 1. 显式校验（返回 true 表示通过，其他表示失败）
    const validateResult = await formRef.current?.validate();
    console.log('校验结果:', validateResult);
    
    // 检查校验是否通过（TDesign 1.8.0 validate 返回 true 或错误对象）
    const hasErrors = validateResult !== true && Object.keys(validateResult).length > 0;
    
    if (hasErrors) {
      MessagePlugin.warning('请完善表单信息');
      return; // 不关闭弹窗
    }
    
    // 2. 获取值
    const values = formRef.current?.getFieldsValue(true);
    
    // 3. 转换数据
    const submitData = {
      ...values,
      userStatus: values.userStatus ? 1 : 0,
      roleIds: Array.isArray(values.roleIds) ? values.roleIds.map(Number) : [],
    };
    
    // 4. 提交
    try {
      if (currentUser) {
        await userApi.update(currentUser.id, submitData);
        MessagePlugin.success('更新成功');
      } else {
        await userApi.create(submitData);
        MessagePlugin.success('创建成功');
      }
      
      setVisible(false);
      setCurrentUser(null)
      formRef.current?.reset();
      fetchUsers();
    } catch (error) {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleResetSubmit = async () => {
    const validateResult = await resetForm.validate();
    
    if (validateResult !== true && Object.keys(validateResult).length > 0) {
      MessagePlugin.warning('请输入符合要求的密码');
      return;
    }
    
    const values = resetForm.getFieldsValue(true);
    
    try {
      if (currentUser) {
        await userApi.resetPassword(currentUser.id, values.password);
        MessagePlugin.success('密码重置成功');
        setResetVisible(false);
        resetForm.reset();
      }
    } catch (error) {
      MessagePlugin.error('密码重置失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await userApi.delete(id);
      MessagePlugin.success('删除成功');
      fetchUsers();
    } catch (error) {
      MessagePlugin.error('删除失败');
    }
  };

  const handleStatusChange = async (record: AdminUser, checked: boolean) => {
    try {
      await userApi.updateStatus(record.id, checked ? 1 : 0);
      MessagePlugin.success('状态更新成功');
      fetchUsers();
    } catch (error) {
      MessagePlugin.error('状态更新失败');
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchUsers({ pageNum: 1 });
  };

  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchUsers({ pageNum: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ ...pagination, current: page, pageSize });
    fetchUsers({ pageNum: page, pageSize });
  };

  const columns : PrimaryTableCol<AdminUser>[] =  [
    { title: '用户名', colKey: 'username', width: 120 },
    { title: '昵称', colKey: 'nickname', width: 120 },
    { title: '邮箱', colKey: 'email', width: 180 },
    { title: '手机号', colKey: 'phone', width: 130 },
    {
      title: '角色',
      colKey: 'roles',
      width: 200,
      cell: ({ row }: { row: AdminUser }) => (
        <Space>
          {row.roles?.map((role) => (
            <Tag key={role.id} theme="primary" variant="light">{role.roleName}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '类型',
      colKey: 'userType',
      width: 100,
      cell: ({ row }: { row: AdminUser }) => (
        <Tag theme={row.userType === 1 ? 'primary' : 'default'} variant="light">
          {row.userType === 1 ? '管理用户' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '状态',
      colKey: 'userStatus',
      width: 100,
      cell: ({ row }: { row: AdminUser }) => (
        <Switch
          value={row.userStatus === 1}
          onChange={(checked) => handleStatusChange(row, checked)}
          label={['启用', '禁用']}
        />
      ),
    },
    { title: '创建时间', colKey: 'createTime', width: 160 },
    {
      title: '操作',
      colKey: 'action',
      width: 240,
      fixed: 'right',
      cell: ({ row }: { row: AdminUser }) => (
        <Space>
          <Button theme="primary" variant="text" size="small" onClick={() => handleEdit(row)} icon={<EditIcon />}>编辑</Button>
          <Button theme="warning" variant="text" size="small" onClick={() => handleResetPassword(row)} icon={<LockOnIcon />}>重置密码</Button>
          <Popconfirm content="确定删除该用户吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" size="small" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">用户管理</h1>
      
      {/* 搜索区域 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <Form form={searchForm} layout="inline">
          <FormItem name="username" label="用户名">
            <Input placeholder="请输入用户名" prefixIcon={<SearchIcon />} />
          </FormItem>
          <FormItem name="nickname" label="昵称">
            <Input placeholder="请输入昵称" />
          </FormItem>
          <FormItem name="phone" label="手机号">
            <Input placeholder="请输入手机号" />
          </FormItem>
          <FormItem name="status" label="状态">
            <Select placeholder="请选择状态" clearable style={{ width: 120 }}>
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </FormItem>
          <FormItem>
            <Button theme="primary" onClick={handleSearch}>查询</Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>重置</Button>
          </FormItem>
        </Form>
      </div>

      <div className="mb-4">
        <Button theme="primary" onClick={handleAdd} icon={<AddIcon />}>新增用户</Button>
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-lg overflow-x-auto">
        <Table data={users} 
          columns={columns} 
          loading={loading} rowKey="id" 
          bordered hover stripe
          pagination={{
            ...pagination,
            onChange: (pageInfo: any) => handlePageChange(pageInfo.current, pageInfo.pageSize),
          }} />
      </div>

      {/* ===================== 新增/编辑弹窗（完全自定义按钮）===================== */}
      <Dialog
        header={currentUser ? '编辑用户' : '新增用户'}
        visible={visible}
        onClose={() => { setVisible(false); resetFormState(); }}
        footer={null}  // ✅ 必须设为 null，禁用所有自带按钮
        width={600}
        key={currentUser ? `edit-${currentUser.id}` : 'add-new'}
      >
        <Form ref={formRef}
            initialData={currentUser || {}} labelWidth={100}>
          <Form.FormItem label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input placeholder="请输入用户名" disabled={!!currentUser} />
          </Form.FormItem>
          
          {!currentUser && (
            <Form.FormItem label="密码" name="password" rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码至少6位' }]}>
              <Input type="password" placeholder="请输入密码" />
            </Form.FormItem>
          )}
          
          <FormItem label="昵称" name="nickname" rules={[{ required: true, message: '请输入昵称' }]}>
            <Input placeholder="请输入昵称" />
          </FormItem>
          
          <FormItem label="邮箱" name="email" rules={[{ required: true, message: '请输入邮箱' }, { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '邮箱格式不正确' }]}>
            <Input placeholder="请输入邮箱" />
          </FormItem>
          
          <FormItem label="手机号" name="phone" rules={[{ required: true, message: '请输入手机号' }, { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }]}>
            <Input placeholder="请输入手机号" />
          </FormItem>
          
          <FormItem label="角色" name="roleIds" rules={[{ required: true, message: '请选择至少一个角色' }]}>
            <Select multiple placeholder="请选择角色" style={{ width: '100%' }}>
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>{role.roleName}</Option>
              ))}
            </Select>
          </FormItem>
          
          <FormItem label="类型" name="userType" initialValue={2}>
            <Radio.Group>
              <Radio value={1}>管理用户</Radio>
              <Radio value={2}>普通用户</Radio>
            </Radio.Group>
          </FormItem>
          
          <FormItem label="状态" name="userStatus" initialValue={true}>
            <Switch label={['启用', '禁用']} />
          </FormItem>
        </Form>

        {/* ✅ 完全自定义底部按钮，普通 Button 的 onClick 手动调用校验 */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button theme="default" variant="outline" onClick={() => { setVisible(false); form.reset(); }}>
            取消
          </Button>
          <Button theme="primary" onClick={handleFormSubmit}>
            {currentUser ? '保存修改' : '确认创建'}
          </Button>
        </div>
      </Dialog>

      {/* ===================== 重置密码弹窗（完全自定义按钮）===================== */}
      <Dialog
        header="重置密码"
        visible={resetVisible}
        onClose={() => { setResetVisible(false); resetForm.reset(); }}
        footer={null}  // ✅ 禁用自带按钮
        width={400}
      >
        <Form form={resetForm} labelWidth={80}>
          <FormItem label="新密码" name="password" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码至少6位' }]}>
            <Input type="password" placeholder="请输入新密码" />
          </FormItem>
        </Form>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <Button theme="default" variant="outline" onClick={() => { setResetVisible(false); resetForm.reset(); }}>
            取消
          </Button>
          <Button theme="primary" onClick={handleResetSubmit}>
            确认重置
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
